import { LandingLayout } from '@/components/layout';
import { ExperienceList, SearchBar, CategoryFilter, StayDateHeader } from '@/components/experiences';
import { useExperiences } from '@/hooks/useExperiences';
import { usePropertyContext, useWidgetSettings } from '@/context/PropertyContext';
import { useStayContext, useDuringStayMode } from '@/context/StayContext';
import { Sparkles, Search, Loader2 } from 'lucide-react';

/**
 * Landing page - Premium full page experience listing
 * 
 * Features:
 * - Elegant page title with serif typography
 * - "During-stay" mode with smart filtering
 * - Premium loading and empty states
 * - Full WCAG 2.1 AA compliance
 */
export function Landing() {
  const { config, isLoading: configLoading, error: configError } = usePropertyContext();
  const settings = useWidgetSettings();
  const { experiences, isLoading, error, isFilteredByStay } = useExperiences();
  const { isDuringStayMode, daysUntilStayEnds } = useDuringStayMode();
  const { mode, setStayDates, setMode } = useStayContext();

  // Handle date changes from the header
  const handleDatesChange = (startDate: string | null, endDate: string | null) => {
    setStayDates(startDate, endDate);
    if (!startDate || !endDate) {
      setMode('browse');
    } else {
      setMode('during-stay');
    }
  };

  if (configLoading) {
    return (
      <LandingLayout>
        <LoadingState />
      </LandingLayout>
    );
  }

  if (configError) {
    return (
      <LandingLayout>
        <ErrorState message={configError} />
      </LandingLayout>
    );
  }

  return (
    <LandingLayout>
      {/* Stay Date Header - shown in during-stay mode */}
      {(isDuringStayMode || mode === 'during-stay') && (
        <StayDateHeader
          className="mb-10"
          showDatePicker={true}
          onDatesChange={handleDatesChange}
        />
      )}

      {/* Page Title - Elegant serif typography */}
      <div className="mb-12 animate-fade-in">
        <div className="flex items-center gap-4 mb-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-primary/10 to-primary/5 shadow-soft">
            <Sparkles className="h-6 w-6 text-primary" />
          </div>
          <h2 className="font-serif text-4xl font-semibold tracking-luxury text-foreground">
            {isDuringStayMode ? 'Experiences During Your Stay' : 'Explore Experiences'}
          </h2>
        </div>
        <p className="text-muted-foreground ml-16 max-w-2xl text-base leading-relaxed">
          {isFilteredByStay && experiences.length > 0
            ? `${experiences.length} experience${experiences.length !== 1 ? 's' : ''} available during your stay`
            : config?.experienceCount
              ? `Discover ${config.experienceCount} unique local activities curated by ${config.property?.name || 'us'}`
              : 'Discover unique local activities and curated experiences'}
        </p>
      </div>

      {/* Filters - Premium styling */}
      <div className="mb-10 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between animate-fade-in" style={{ animationDelay: '100ms' }}>
        <CategoryFilter />
        {settings.enableSearch && <SearchBar className="w-full sm:max-w-xs" />}
      </div>

      {/* Error State */}
      {error && <ErrorState message={error} />}

      {/* No Results State for during-stay mode */}
      {!isLoading && !error && experiences.length === 0 && isFilteredByStay && (
        <NoExperiencesForStay onClearFilter={() => handleDatesChange(null, null)} />
      )}

      {/* Experience List */}
      <ExperienceList 
        experiences={experiences} 
        isLoading={isLoading}
        emptyMessage={
          isFilteredByStay 
            ? "No experiences available during your stay dates" 
            : undefined
        }
      />

      {/* Filtered results indicator */}
      {isFilteredByStay && experiences.length > 0 && (
        <FilteredResultsFooter 
          count={experiences.length}
          daysLeft={daysUntilStayEnds}
        />
      )}
    </LandingLayout>
  );
}

function LoadingState() {
  return (
    <div className="flex flex-col items-center justify-center py-24">
      <div className="relative">
        <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center shadow-soft">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
      <p className="mt-6 text-sm text-muted-foreground font-medium">Loading experiences...</p>
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-8 text-center animate-fade-in shadow-soft">
      <p className="text-destructive font-semibold">{message}</p>
    </div>
  );
}

interface NoExperiencesForStayProps {
  onClearFilter: () => void;
}

function NoExperiencesForStay({ onClearFilter }: NoExperiencesForStayProps) {
  return (
    <div className="rounded-2xl border-2 border-dashed border-muted-foreground/15 bg-muted/20 p-12 text-center animate-fade-in">
      <div className="mx-auto max-w-md">
        <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-2xl bg-muted shadow-soft">
          <Search className="h-8 w-8 text-muted-foreground/40" strokeWidth={1.5} />
        </div>
        <h3 className="font-serif text-2xl font-semibold text-foreground mb-3 tracking-luxury">
          No experiences available for your dates
        </h3>
        <p className="text-sm text-muted-foreground mb-8 leading-relaxed">
          Unfortunately, there are no experiences with availability during your stay.
          Try adjusting your dates or browse all experiences.
        </p>
        <button
          onClick={onClearFilter}
          className="btn-luxury shadow-soft hover:shadow-luxury"
        >
          Browse all experiences
        </button>
      </div>
    </div>
  );
}

interface FilteredResultsFooterProps {
  count: number;
  daysLeft: number | null;
}

function FilteredResultsFooter({ count, daysLeft }: FilteredResultsFooterProps) {
  return (
    <div className="mt-12 text-center animate-fade-in">
      <div className="inline-flex items-center gap-3 rounded-full bg-muted/40 px-6 py-3 text-sm text-muted-foreground font-medium shadow-soft">
        <span>
          Showing {count} experience{count !== 1 ? 's' : ''} available during your stay
        </span>
        {daysLeft !== null && daysLeft <= 7 && (
          <span className="font-bold text-primary">
            {daysLeft === 0 ? '• Book today!' : `• ${daysLeft} day${daysLeft !== 1 ? 's' : ''} left`}
          </span>
        )}
      </div>
    </div>
  );
}
